import { useCallback, useEffect, useRef, useState } from 'react';

export type HealthStatus = 'checking' | 'online' | 'offline';

const POLL_MS = 30_000;
const TIMEOUT_MS = 8_000;

/**
 * Polls the assistant's health route while `active` (the dock is open) and
 * the tab is visible. A closed dock or a background tab makes no requests.
 *
 * With no URL — a host still rendering an older Blade partial — the status is
 * reported as online, which is what the indicator always showed before.
 */
export function useHealth(healthUrl: string, active: boolean) {
  const [status, setStatus] = useState<HealthStatus>(healthUrl ? 'checking' : 'online');
  const inFlight = useRef<AbortController | null>(null);

  const check = useCallback(async () => {
    if (!healthUrl) return;

    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const res = await fetch(healthUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (inFlight.current === controller) setStatus(res.ok ? 'online' : 'offline');
    } catch {
      // A superseded check was aborted on purpose; only the latest one counts.
      if (inFlight.current === controller) setStatus('offline');
    } finally {
      window.clearTimeout(timer);
      if (inFlight.current === controller) inFlight.current = null;
    }
  }, [healthUrl]);

  useEffect(() => {
    if (!active || !healthUrl) return;

    const tick = () => {
      if (document.visibilityState === 'visible') void check();
    };

    tick();
    const interval = window.setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('online', tick);
    window.addEventListener('offline', tick);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('online', tick);
      window.removeEventListener('offline', tick);
      inFlight.current?.abort();
      inFlight.current = null;
    };
  }, [active, healthUrl, check]);

  return { status, recheck: check };
}
